import { useMutation, useQueryClient } from '@tanstack/react-query'
import { explainShareError } from './explainShareError'
import { publicArtifactsOn } from './publicFlag'
import { useState } from 'react'
import { artifactShare } from '../transport/api'
import { copyText } from '../ui/clipboard'
import { canShareLink, shareLink } from '../ui/shareLink'
import { FriendPicker } from './FriendPicker'
import {
  machineIsReachable,
  type ArtifactLink,
  type ArtifactMachine,
  type ArtifactRow,
  type ArtifactVisibility,
} from './everywhere'

/**
 * What the Friends option says underneath it.
 *
 * "You and your Avengents friends" stopped being true the moment the owner
 * could pick which ones, and a label that claims a wider audience than the
 * setting has is the one mistake this whole feature is about.
 */
function whoFriends(row: Pick<ArtifactRow, 'allow'>): string {
  if (row.allow === undefined) return 'Everyone you are friends with · tap to choose'
  if (row.allow.length === 0) return 'Nobody chosen yet · tap to choose'
  return `${row.allow.length} chosen friend${row.allow.length === 1 ? '' : 's'} · tap to change`
}

/**
 * WHAT THE PERSON MAY CHANGE ABOUT ONE ARTIFACT: who opens it, and how to hand
 * the link out.
 *
 * Owner, 2026-09-20: 「因為是 In-App Browser,我們可以在 Header Bar 也是最上方,增加讓
 * 使用者可以設定的這些方法,比如說 Share Button、使用者權限的開啟,這些功能的設定都可以
 * 使用一個下拉選單來調整」. So this is the CONTENT of the viewer's header menu. It
 * was a sheet of its own until the viewer existed and the sheet had no door left;
 * it is one component rather than two copies because the rule here is 一個想法只留
 * 一份, and a settings panel that exists twice drifts within a week.
 *
 * ⚠️ IT READS THE LIST, NOT THE ROW IT WAS OPENED WITH. The first version
 * rendered from the snapshot the rail handed it, so the highlight came from a
 * value that could not change while it was open: a write landed, the list
 * refreshed underneath, and it went on showing the old answer. The owner reported
 * it as 「這三個按鈕好像無效…點擊某一個按鈕,它應該要 highlight 才對」, and he was
 * right — a control whose state cannot move IS a dead button, whatever is
 * happening behind it. The caller passes the LIVE row and its machine.
 *
 * THE OFFLINE RULE IS NOT A STYLE CHOICE. An artifact's visibility lives in a
 * file on the machine that publishes it, and the only way to change it is to ask
 * that machine. With the machine away there is nothing to ask, so the dial is
 * disabled and says why. And "we cannot see that machine" is NOT the same
 * sentence as "that machine is offline" — inventing the second would send someone
 * to go and switch on a machine that is already running.
 */
const DIAL: Array<{ v: ArtifactVisibility; label: string; who: string }> = [
  { v: 'private', label: 'Private', who: 'Only you, signed in' },
  { v: 'friends', label: 'Friends', who: 'You and your Avengents friends, signed in' },
  { v: 'public', label: 'Public', who: 'Anyone with the link' },
]

/**
 * PUBLIC IS LOCKED, AND DELIBERATELY STILL VISIBLE — owner, 2026-09-24: 幫我把
 * product 裡面 artifact 可以放置為 Public 的這一個按鈕的功能先鎖起來, 之後我們會需要加到
 * Premium Feature 裡面或者是 Pro Feature 裡面 … 但是目前上面要顯示這個按鈕, 但只是這個
 * 按鈕是被 disable 的, 讓使用者可以意識到以後有這種功能的存在.
 *
 * SHOWN RATHER THAN REMOVED is the instruction and it is the whole point: a control
 * that is missing teaches nobody it is coming, and a person who has never seen Public
 * cannot want it. So it keeps its place in the dial, greyed, still saying who it would
 * let in — and carries the word that explains why it will not press.
 *
 * A SET RATHER THAN `v === 'public'`, because the next thing that happens to this is a
 * plan check: when Pro exists this becomes "locked unless the account's plan allows it"
 * and the call site does not have to change shape to get there. See [[billing-project]]
 * for where the plan row lives.
 *
 * WHAT THIS IS NOT: enforcement. Disabling a button stops the UI, not the capability —
 * `avengents artifact share <hash> public` still works on the machine itself, because
 * the artifact's visibility is a file on the publishing machine and that machine is the
 * person's own. A paid tier has to be enforced where an anonymous viewer is actually
 * admitted (the relay's viewer token), not here. Told the owner rather than implied.
 */
function lockedSet(): ReadonlySet<ArtifactVisibility> {
  return publicArtifactsOn() ? EMPTY : PUBLIC_ONLY
}
const EMPTY: ReadonlySet<ArtifactVisibility> = new Set<ArtifactVisibility>()
const PUBLIC_ONLY: ReadonlySet<ArtifactVisibility> = new Set<ArtifactVisibility>(['public'])

/** Why it will not press. Shown in place of `who`, so the row still reads as a real
 *  capability that is gated rather than a control that is broken. */
const LOCKED_WHY = 'Coming as a Pro feature'

export function ArtifactSettings({
  row,
  machine,
  link,
}: {
  /** The LIVE row, resolved by the caller from the artifacts cache. */
  row: ArtifactRow
  /** Its machine, or null when this account's machine list does not hold it. */
  machine: ArtifactMachine | null
  link: ArtifactLink | null
}) {
  const qc = useQueryClient()
  /** Which rows this build withholds — read per render so a test can flip the env. */
  const LOCKED = lockedSet()
  const [note, setNote] = useState<string>('')
  /** The friend picker, open — owner, 2026-09-20: Friends is a door now. */
  const [picking, setPicking] = useState(false)
  /** What the person just pressed, shown until the list agrees — see below. */
  const [pending, setPending] = useState<ArtifactVisibility | null>(null)

  const known = machine !== null
  const reachable = known && machineIsReachable(machine)

  const share = useMutation({
    mutationFn: ({ v, allow }: { v: ArtifactVisibility; allow?: string[] | null }) =>
      artifactShare(row.device_id, row.hash, v, allow),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['artifacts'] }),
    // The press stops being shown either way: on success the list carries it,
    // and on failure the truth is the OLD setting, which must come back.
    onSettled: () => setPending(null),
  })

  /*
   * What the dial shows. The pressed option lights immediately — a dial that only
   * moves after a round trip reads as broken on a slow link, which is how this was
   * reported — and falls back to what the machine actually says the moment the
   * answer, or the failure, arrives.
   */
  const shown = pending ?? row.visibility

  return (
    <div className="art-set">
      <h4 className="art-h">Hand it out</h4>
      <div className="art-give">
        {/*
         * The share sheet ONLY where there is one. Rendering it unconditionally
         * and relabelling it "Copy link" on a desktop browser put two buttons
         * with the same word side by side — a fallback is a thing that happens
         * inside one control, not a second copy of it.
         */}
        {canShareLink() && (
          <button
            type="button"
            className="art-give-btn"
            disabled={!row.url}
            onClick={() => {
              /* Straight from the tap: nothing awaited in front of the share
                 sheet, or WebKit refuses it (ui/shareLink.ts). */
              if (!row.url) return
              void shareLink(row.url, row.summary || row.name).then((r) =>
                setNote(
                  r === 'copied' ? 'Link copied' :
                  r === 'failed' ? 'Could not share or copy the link' : '',
                ),
              )
            }}
          >
            Share…
          </button>
        )}
        <button
          type="button"
          className="art-give-btn"
          disabled={!row.url}
          onClick={() => {
            if (!row.url) return
            void copyText(row.url).then((r) => setNote(r.ok ? 'Link copied' : 'Copy failed'))
          }}
        >
          Copy link
        </button>
      </div>
      <p className="art-addr art-mono">{row.url || 'no address yet — the relay has not confirmed one'}</p>
      {note && <p className="art-note">{note}</p>}

      <h4 className="art-h">Who may open it</h4>
      <div className="art-dial" role="group" aria-label="Who may open it">
        {DIAL.map(({ v, label, who }) => {
          const locked = LOCKED.has(v)
          return (
          <button
            key={v}
            type="button"
            className={`art-opt${shown === v ? ' on' : ''}${pending === v ? ' busy' : ''}${locked ? ' locked' : ''}`}
            aria-pressed={shown === v}
            disabled={locked || !reachable || share.isPending}
            title={
              locked
                ? `${who} — ${LOCKED_WHY}`
                : reachable
                  ? who
                  : 'Its machine cannot be reached, so this cannot be changed'
            }
            onClick={() => {
              // `disabled` already stops a pointer; this is the same refusal for the
              // paths that do not go through one.
              if (locked) return
              if (share.isPending) return
              /*
               * FRIENDS IS A DOOR, NOT A SETTING — owner, 2026-09-20: 「把…
               * Friends 那個按鈕改成按下去之後會彈跳出來一個選單讓使用者可以選擇要把
               * 哪些朋友加進去」. It opens the picker even when it is ALREADY the
               * current setting, because "change who" is the common reason to
               * press it a second time; the other two are plain settings and
               * still do nothing when they are already on.
               */
              if (v === 'friends') {
                setPicking(true)
                return
              }
              if (row.visibility === v) return
              setPending(v)
              share.mutate({ v })
            }}
          >
            <span className="art-opt-l">
              {label}
              {locked && <span className="art-opt-pro">Pro</span>}
            </span>
            <span className="art-opt-w">
              {locked ? LOCKED_WHY : v === 'friends' ? whoFriends(row) : who}
            </span>
          </button>
          )
        })}
      </div>

      {known && !reachable && (
        <p className="art-note">
          {/*
            The example names `friends`, not `public`: this line used to hand the reader
            the exact command the dial above now refuses, which would have made the lock
            a suggestion rather than a lock.
          */}
          {row.machine} is offline, so this cannot be changed from here. Turn it on, or run{' '}
          <code>avengents artifact share {row.hash.slice(0, 8)} friends</code> on the machine
          itself.
        </p>
      )}
      {!known && (
        <p className="art-note">
          This app cannot see {row.machine} in the account&rsquo;s machine list right now, so it
          cannot change anything there.
        </p>
      )}
      {share.isError && (
        <p className="art-note art-bad-note">
          That did not take:{' '}
          {share.error ? explainShareError(share.error, row.machine) : 'the machine refused'}
        </p>
      )}
      {picking && (
        <FriendPicker
          value={row.allow ?? null}
          saving={share.isPending}
          error={
            share.isError
              ? share.error instanceof Error
                ? share.error.message
                : 'the machine refused'
              : null
          }
          onCancel={() => setPicking(false)}
          onSave={(allow) => {
            setPending('friends')
            share.mutate({ v: 'friends', allow }, { onSuccess: () => setPicking(false) })
          }}
        />
      )}
      {link?.accepted === false && link.terms_url && (
        <p className="art-note">
          Nothing is served from this account yet — the Artifacts terms have not been accepted.{' '}
          <a href={link.terms_url} target="_blank" rel="noreferrer">
            Read and accept them
          </a>
          .
        </p>
      )}
    </div>
  )
}
